import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { parseISO } from 'date-fns';
import type { ReadonlyDeep } from 'type-fest';

import { DatePicker } from '@mui/x-date-pickers';
import Stack from '@mui/material/Stack';

import { EMPTY_STRING } from '@e-socna/empty';
import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { MissionArchiveInfoInput } from '@e-socna/data-layer';

import { CheckboxList } from '../../components/CheckboxList';
import { FreeTextMultiAutocomplete } from '../../components/FreeTextMultiAutocomplete';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';

import { useInitialValuesFactory } from './hooks/useInitialValuesFactory.hook';
import { missionArchiveInfoForm2ApiMapper } from './mappers/mission-archive-info.form2api.mapper';
import { isMissionArchiveInfoComplete } from './utils/mission-archive-info-complete.util';

interface MissionArchiveInfoFormValues {
  readonly actionPayload?: Readonly<{
    archiveInfo: ReadonlyDeep<MissionArchiveInfoInput>;
  }> | null;
}

const UPLOAD_CHECKLIST = [
  {
    id: 'iHavedCheckedThatReportHasBeenUploaded',
    label: 'Le rapport a bien été déposé',
  },
  {
    id: 'iHavedCheckedThatPlansHaveBeenUploaded',
    label: 'Les plans ont bien été déposés',
  },
  {
    id: 'iHavedCheckedThatQuotationHasBeenUploaded',
    label: 'Le devis a bien été déposé',
  },
  {
    id: 'iHavedCheckedThatHypothesisNotesHaveBeenUploaded',
    label: 'Les notes de calcul ont bien été déposées',
  },
] as const;

type UploadChecklistId = (typeof UPLOAD_CHECKLIST)[number]['id'];

export const MissionArchiveInfoDialog_Mission_Fragment = graphql(`
  fragment MissionArchiveInfoDialog_Mission_Fragment on Mission {
    businessCreationDate
  }
`);

export const MissionArchiveInfoFields = () => {
  const { archiveBinders } = useReferencesContext();
  const { mission: rawMission } = useMissionReadyContext();
  const { businessCreationDate } = getFragmentData(
    MissionArchiveInfoDialog_Mission_Fragment,
    rawMission,
  );

  const { setFieldValue } = useFormikContext<MissionArchiveInfoFormValues>();

  const { initialValues } = useInitialValuesFactory({
    startDateForAnalytics: businessCreationDate,
  });

  const [draft, setDraft] = useState(initialValues);

  const isComplete = isMissionArchiveInfoComplete(draft);

  useEffect(() => {
    void setFieldValue(
      'actionPayload',
      isComplete
        ? { archiveInfo: missionArchiveInfoForm2ApiMapper(draft) }
        : null,
    );
  }, [draft, isComplete, setFieldValue]);

  const archiveBinderSuggestions = archiveBinders.map(({ label }) => label);

  const startDateForAnalyticsValue = useMemo(
    () =>
      draft.startDateForAnalytics
        ? parseISO(draft.startDateForAnalytics)
        : null,
    [draft.startDateForAnalytics],
  );

  const endDateForAnalyticsValue = useMemo(
    () =>
      draft.endDateForAnalytics ? parseISO(draft.endDateForAnalytics) : null,
    [draft.endDateForAnalytics],
  );

  const handleStartDateChange = useCallback((date: Date | null) => {
    setDraft((previous) => ({
      ...previous,
      startDateForAnalytics: date ? date.toISOString() : EMPTY_STRING,
    }));
  }, []);

  const handleEndDateChange = useCallback((date: Date | null) => {
    setDraft((previous) => ({
      ...previous,
      endDateForAnalytics: date ? date.toISOString() : EMPTY_STRING,
    }));
  }, []);

  const handleArchiveBindersChange = useCallback(
    (labels: readonly string[]) => {
      setDraft((previous) => ({
        ...previous,
        archiveBinders: labels,
      }));
    },
    [],
  );

  const checkedUploads = UPLOAD_CHECKLIST.filter(({ id }) => draft[id]).map(
    ({ id }) => id,
  );

  const handleUploadChecklistChange = useCallback(
    (checkedIds: readonly UploadChecklistId[]) => {
      setDraft((previous) => ({
        ...previous,
        ...Object.fromEntries(
          UPLOAD_CHECKLIST.map(({ id }) => [id, checkedIds.includes(id)]),
        ),
      }));
    },
    [],
  );

  return (
    <Stack spacing={2}>
      <DatePicker
        label="Date de début pour analytics"
        maxDate={endDateForAnalyticsValue ?? undefined}
        onChange={handleStartDateChange}
        slotProps={{ textField: { required: true } }}
        value={startDateForAnalyticsValue}
      />
      <DatePicker
        label="Date de fin pour analytics"
        minDate={startDateForAnalyticsValue ?? undefined}
        onChange={handleEndDateChange}
        slotProps={{ textField: { required: true } }}
        value={endDateForAnalyticsValue}
      />
      <FreeTextMultiAutocomplete
        id="mission-archive-info-dialog-archive-binders"
        label="Classeurs d'archive"
        onChange={handleArchiveBindersChange}
        required
        suggestions={archiveBinderSuggestions}
        value={draft.archiveBinders}
      />
      <CheckboxList<UploadChecklistId>
        idPrefix="mission-archive-info-uploads-"
        items={UPLOAD_CHECKLIST}
        onChange={handleUploadChecklistChange}
        value={checkedUploads}
        variant="edition"
      />
    </Stack>
  );
};
